import {Inject, Injectable, InjectionToken} from "@angular/core";
import {JwtHttpClientService} from "../../http/jwt-http-client.service";
import {ConfigurationService} from "../../configuration/configuration.service";
import {ResourceModule} from "../resource.module";
import {HttpHeaders, HttpResponse} from "@angular/common/http";
import {firstValueFrom, map, Observable} from "rxjs";
import {ResourceService} from "../resource.service";

/**
 * Generic service for performing an action on the server.
 *
 * The #ACTION_RESOURCE must be registered as a provider in the Angular dependency injection to specify the resource name.
 * This will form part of the URL path.
 *
 * Errors are thrown as a #DataClientError
 */
@Injectable()
export class ActionService extends ResourceService {

    /**
     * Static URL prefix for all server side request calls
     * @type {string}
     */
    public static readonly URL_PREFIX = '/dataserver/action/';

    /**
     * Provider lookup key alias that is used during dependency injection
     * @type {InjectionToken}
     */
    public static readonly ACTION_RESOURCE: InjectionToken<string> = new InjectionToken<string>('actionResource');

    /**
     * Provider lookup key alias that is used during dependency injection
     * @type {InjectionToken}
     */
    public static readonly RESOURCE_VERSION: InjectionToken<string> = new InjectionToken<string>('resourceVersion');

    /**
     * URL root to access the request resource ending in '/'
     */
    protected readonly url: string;

    /**
     * Constructs the service with a resource, Http service, and Error service.
     * The resource may be injected via the #ACTION_RESOURCE alias
     * @param http HTTP Service
     * @param configService
     * @param nameSpace NameSpace that forms part of the URL
     * @param actionNames Action names for the APP
     * @param resource Resource name that forms part of the URL
     * @param useAsync
     */
    constructor(http: JwtHttpClientService,
                @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) nameSpace: string,
                @Inject(ActionService.ACTION_RESOURCE) resource: string,
                protected configService: ConfigurationService,
                @Inject(ResourceModule.RESOURCE_USE_ASYNC) protected useAsync: boolean,
                @Inject(ResourceModule.ACTION_NAMES_KEY) protected actionNames: string[]) {
        super(http, nameSpace, resource);

        if (actionNames && actionNames.indexOf(resource) < 0) {
            throw new Error("Action " + resource + " is not supported.");
        }

        let url: string = ActionService.URL_PREFIX + nameSpace + "/" + resource;

        let version: number = this.getResourceVersion();
        if (version) {
            url = url + ".v" + version;
        }
        url = url + "/";

        this.url = url;
    }

    /**
     * Performs an action on the server
     * @param entityData
     * @returns {Promise<any>} JSON object representation of the action result
     */
    public async performAction(entityData: any): Promise<any> {
        return firstValueFrom((this.useAsync
            ? this.handleAsync(this.http.post(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData, undefined, new HttpHeaders({"async": "true"})))
            : this.http.post(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData)).pipe(
            map((response: HttpResponse<Object | Blob | string>) => response.body || {})
        ));
    }

    /**
     * Performs an action on the server to download the results
     * @param entityData
     */
    public async performDownloadAction(entityData: any): Promise<{ fileName: string, content: Blob }> {
        return firstValueFrom((this.useAsync
            ? <Observable<HttpResponse<Blob>>>this.handleAsync(this.http.post(this.url + "download/", entityData.toJSONString ? entityData.toJSONString() : entityData, undefined, new HttpHeaders({"async": "true"}), "blob"), "blob")
            : <Observable<HttpResponse<Blob>>>this.http.post(this.url + "download/", entityData.toJSONString ? entityData.toJSONString() : entityData, undefined, undefined, "blob")).pipe(
            map((response: HttpResponse<Blob>) => ({
                fileName: response.headers.get("content-disposition")?.split(";")[1].split("=")[1].replaceAll("\"", ""),
                content: response.body
            }))
        ));
    }

    public async canPerformAction(): Promise<boolean> {
        return this.configService.getString("resourcePrivilege/action/" + this.nameSpace + "/" + this.resource).then((privMask: string) => privMask ? this.configService.getBoolean("authorisation/" + privMask.replace("*", "perform")) : false);
    }

    public static async canPerformAction(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/action/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "perform")) : false);
    }

    /**
     * Returns the version of the resource.
     * @returns {number}
     */
    getResourceVersion(): number {
        return 0;
    }
}
